import { AfterViewInit, Directive, ElementRef, Host, HostListener, Input, OnDestroy, OnInit, Optional, Renderer2, SkipSelf } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, Validators } from '@angular/forms';
import { AvanoFormService } from '@avano/avano-components/common/avano-form.service';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[avanoInputText]'
})
export class InputTextDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  formControl: FormControl | AbstractControl;

  @Input()
  formControlName: string;

  @Input()
  tooltip: string;

  @Input()
  rightAligned: boolean;

  @Input()
  clearEnabled = true; // If you want an input field without a clear
  // (e.g. having a different directive there or it should not be possible to clear it)

  // If you want to generate an avano-compliant label, use this here
  // It's a setter to support dynamic changes of label
  @Input() set label(label: string) {
    this._label = label;
    this.handleLabel();
  }

  private _label: string;

  /** The HTML-Anchor-Element that functions as the button to clear the input field. */
  anchorElement: HTMLElement;
  anchorIconElement: HTMLElement;

  /** The container-element around the input and the clear-icon */
  containerElement: HTMLElement;

  private labelElement: HTMLLabelElement;

  private clearIconClass = 'icon-content-cancel';

  private ngControlSubscription: Subscription;

  constructor(
    public el: ElementRef,
    public renderer: Renderer2,
    public avanoFormService: AvanoFormService,
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer
  ) {
    renderer.addClass(el.nativeElement, 'avano-input-text');
  }

  ngOnInit(): void {
    if (this.controlContainer && this.formControlName && !this.formControl) {
      this.formControl = this.controlContainer.control.get(this.formControlName);
      // If you dynamically add validators to a formControl/formControlname, then
      // the statusChanges are triggered ... and as we automatically want to render a required
      // icon for required fields we need to react to such changes
      this.ngControlSubscription = this.formControl.statusChanges.subscribe(() => {
        this.handleLabel();
      });
    }
  }

  ngAfterViewInit(): void {
    this.createContainerElement();

    this.createClearIcon();

    // Add the event listener for the mousedown event. The click event does not work because blur triggers before click.
    this.anchorElement.addEventListener('mousedown', () => this.onClearButtonClick());

    if (this.labelElement) {
      this.el.nativeElement.parentElement.insertBefore(this.labelElement, this.el.nativeElement);
      this.handleLabel();
    }

    // Insert the container before the input element
    this.el.nativeElement.parentElement.insertBefore(this.containerElement, this.el.nativeElement);

    // Put the input element and anchor inside the container.
    this.containerElement.appendChild(this.el.nativeElement);
    if (this.clearEnabled) {
      this.containerElement.appendChild(this.anchorElement);
    }
  }

  ngOnDestroy() {
    if (this.ngControlSubscription) {
      this.ngControlSubscription.unsubscribe();
    }
  }

  @HostListener('keyup')
  onKeyUp(): void {
    if (this.el.nativeElement.value !== '') {
      this.renderer.removeClass(this.containerElement, 'clear-input-empty');
    } else {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.isReadOnly()) {
      this.renderer.addClass(this.containerElement, 'clear-input-active');
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.renderer.removeClass(this.containerElement, 'clear-input-active');
    if (this.isInputEmpty()) {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.isReadOnly()) {
      this.renderer.addClass(this.containerElement, 'clear-input-hover');
      this.setStyleClassForInput();
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseOut(event: any): void {
    // We do not want to remove our styleclasses if the user is hovering the child (clear-icon)
    if (event.relatedTarget !== this.anchorIconElement && event.relatedTarget !== this.anchorElement) {
      this.renderer.removeClass(this.containerElement, 'clear-input-hover');
      this.setStyleClassForInput();
    }
  }

  private setStyleClassForInput() {
    if (!this.isReadOnly()) {
      if (this.isInputEmpty()) {
        this.renderer.addClass(this.containerElement, 'clear-input-empty');
      } else {
        this.renderer.removeClass(this.containerElement, 'clear-input-empty');
      }
    }
  }

  private isInputEmpty(): boolean {
    return this.formControl ? !this.formControl.value : !this.el.nativeElement.value;
  }

  private onClearButtonClick(): void {
    if (this.formControl) {
      this.formControl.markAsDirty();
      this.formControl.setValue('');
    } else {
      this.renderer.addClass(this.containerElement, 'clear-input-empty');
      this.renderer.setProperty(this.el.nativeElement, 'value', '');
      this.el.nativeElement.dispatchEvent(new CustomEvent('input', { bubbles: true }));
    }
  }

  private createContainerElement(): void {
    this.containerElement = document.createElement('span');
    this.renderer.addClass(this.containerElement, 'input-text-container');
    this.renderer.addClass(this.containerElement, 'clear-input-empty');

    if (this.isReadOnly()) {
      this.renderer.addClass(this.containerElement, 'clear-input-readonly');
    }
    if (this.rightAligned) {
      this.renderer.addClass(this.containerElement, 'clear-input-rightaligned');
    }
  }

  private isReadOnly(): boolean {
    return (this.el.nativeElement as HTMLInputElement).readOnly;
  }

  private createClearIcon(): void {
    this.anchorElement = document.createElement('span');
    this.renderer.addClass(this.anchorElement, 'clear-input-button');
    this.anchorIconElement = document.createElement('i');
    this.renderer.addClass(this.anchorIconElement, this.clearIconClass);

    this.anchorElement.appendChild(this.anchorIconElement);
  }

  private handleLabel(): void {
    if (!this.labelElement && this._label) {
      this.labelElement = this.avanoFormService.generateFormLabelElement();
    }
    if (this.labelElement && this._label) {
      this.renderer.setProperty(this.labelElement, 'innerHTML', this._label);

      if (this.el.nativeElement.required || this.formControl?.hasValidator(Validators.required)) {
        this.renderer.appendChild(this.labelElement, this.avanoFormService.generateRequiredElement());
      }

      if(this.tooltip) {
        this.renderer.appendChild(this.labelElement, this.avanoFormService.generateTooltipElement(this.tooltip));
      }
    }
  }
}
